import { useHookstate } from "@hookstate/core";
import { useCallback, useEffect, useRef, useState } from "react";
import ArrowRightWithBody from "../../assets/icons/ArrowRightWithBody";
import Edit from "../../assets/icons/Edit";
import Home from "../../assets/icons/Home";
import Plus from "../../assets/icons/Plus";
import Star from "../../assets/icons/Star";
import { useUsersWithDemerits } from "../../hooks/demerit";
import { demeritStatusMapper } from "../../misc/constants";
import {
  getDateOnly,
  onInputChangeNative,
  socialTranscriptItemsReduce,
  updateLocalStateRaw,
  userHasRole,
} from "../../misc/utils";
import {
  Demerit,
  MainUserWithDemeritsAndSocialTranscriptItems,
} from "../../types";
import { Avatar } from "../Avatar";
import { HasRole } from "../HasRole";
import Modal from "../Modal/Modal";
import DefaultButton from "../default-button/DefaultButton";
import InputField from "../input-field/InputField";
import { DisciplinaryDemerit } from "./DisciplinaryDemerit";
import { useUsersWithSocialScoreAndDemerits } from "../../hooks/demerit-typed";
import { useDebounce } from "../../hooks/ui";
import { useGlobalUserState } from "../../stores/user";

const categoryList = [
  {
    label: "",
    additionalClassName: "text-left",
  },
  {
    label: "Студент",
    additionalClassName: "text-left",
  },
  {
    label: "Социальный транскрипт",
    additionalClassName: "text-right",
  },
  {
    label: "Оценка комнаты",
    additionalClassName: "text-left",
  },
  {
    label: "Количество нарушений",
    additionalClassName: "text-left",
  },
  {
    label: "Почта",
    additionalClassName: "text-left",
  },
  // {
  //   label: 'Пол',
  //   additionalClassName: 'text-left'
  // },
];

const stats = [
  {
    label: "Социальный транскрипт",
    value: "315",
    valueStyle: "text-blue-500",
    icon: "⭐", // Placeholder for an actual icon
  },
  {
    label: "ГПА",
    value: "2,5 / 4",
    valueStyle: "text-green-500",
    icon: "📊", // Placeholder for an actual icon
  },
  {
    label: "Средняя оценка",
    value: "88.8%",
    valueStyle: "text-green-500",
    icon: "🔄", // Placeholder for an actual icon
  },
  {
    label: "Оценка комнаты",
    value: "4 / 5",
    valueStyle: "text-green-500",
    icon: "🏠", // Placeholder for an actual icon
  },
];

export type DisciplinaryTableStudentsState = {
  files: Record<string, File | null>;
  search?: string;
  isAddDemeritModalOpen?: boolean;
  demeritDescription?: string;
  demeritTypeSelected?: string;
  demeritIncidentLink?: string;
  selectedStuden?: number;
  studentsWithDemeritsTriggerFetch?: number;
  selectedDemerit?: Demerit;
  page?: number;
  usersList?: MainUserWithDemeritsAndSocialTranscriptItems[];
  loading?: boolean;
  total?: number;
};

const DisciplinaryTableStudents = () => {
  const userState = useGlobalUserState();
  const user = userState.get();
  const uniqUser = user.uniqUser || {};
  const localState = useHookstate<DisciplinaryTableStudentsState>({
    files: {},
    page: 0,
    loading: false,
    total: 0,
  });
  const state = localState.get();
  const search = state.search || "";
  const usersListRef = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebounce(search, 1000);
  const page = state.page || 0;
  const isAddDemeritModalOpen = Boolean(state.isAddDemeritModalOpen);
  const demeritDescription = state.demeritDescription || "";
  const demeritTypeSelected = state.demeritTypeSelected || "";
  const studentsWithDemeritsTriggerFetch =
    state.studentsWithDemeritsTriggerFetch || 0;
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [multipleSelectedStudents, setMultipleSelectedStudents] = useState<
    MainUserWithDemeritsAndSocialTranscriptItems[]
  >([]);
  const [selectedStudent, setSelectedStudent] =
    useState<MainUserWithDemeritsAndSocialTranscriptItems | null>(null);

  useEffect(() => {
    if (isAddDemeritModalOpen) {
      localState.files.set({});
    }
  }, [isAddDemeritModalOpen]);

  const postUpdateStudentsWithDemerits = (
    newList: MainUserWithDemeritsAndSocialTranscriptItems[]
  ) => {
    const found = newList.find(
      (item: MainUserWithDemeritsAndSocialTranscriptItems) =>
        item.id === selectedStudent?.id
    );
    if (found) {
      setSelectedStudent(found);
    }
  };

  const toggleSelectAllStudents = () => {
    if (multipleSelectedStudents?.length > 0) {
      setMultipleSelectedStudents([]);
    } else {
      setMultipleSelectedStudents(studentsWithDemerits);
    }
  };

  const handleMultipleSelectStudents = (
    student: MainUserWithDemeritsAndSocialTranscriptItems
  ) => {
    if (!multipleSelectedStudents) {
      setMultipleSelectedStudents([student]);
      return;
    }
    const isStudentAlreadySelected = multipleSelectedStudents.find(
      (s) => s.id === student.id
    );
    if (isStudentAlreadySelected) {
      const filtered = multipleSelectedStudents.filter(
        (s) => s.id !== student.id
      );
      setMultipleSelectedStudents(filtered.length > 0 ? filtered : []);
    } else {
      setMultipleSelectedStudents([...multipleSelectedStudents, student]);
    }
    console.log(multipleSelectedStudents);
  };

  // const allStudentsWithDemerits: MainUserWithDemeritsAndSocialTranscriptItems[] = useUsersWithDemerits(studentsWithDemeritsTriggerFetch, postUpdateStudentsWithDemerits)

  // const studentsWithDemerits = allStudentsWithDemerits.filter(d => d.json_saved_data?.email?.toLowerCase().includes(search.toLowerCase()) || d.json_saved_data?.firstname?.toLowerCase().includes(search.toLowerCase()) || d.json_saved_data?.lastname?.toLowerCase().includes(search.toLowerCase()))

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    localState.merge({
      search: e.target.value,
      page: 0,
    });
  };

  const [allStudentsWithDemerits, allStudentsLoading, allStudentsTotal] =
    useUsersWithSocialScoreAndDemerits<"demerits">(
      studentsWithDemeritsTriggerFetch,
      {
        page,
        type: "demerits",
        usersWhere: {
          query: debouncedQuery,
        },
      },
      postUpdateStudentsWithDemerits
    );

  useEffect(() => {
    localState.merge({
      loading: allStudentsLoading,
      total: allStudentsTotal,
    });
  }, [allStudentsLoading, allStudentsTotal]);

  const studentsWithDemerits = allStudentsWithDemerits;

  const handleArrowClick = (
    student: MainUserWithDemeritsAndSocialTranscriptItems
  ) => {
    if (selectedStudent?.id === student.id) {
      setIsCollapsed(false);
      setSelectedStudent(null);
    } else {
      setSelectedStudent(student);
      setIsCollapsed(true);
    }
  };

  const openDemeritModal = () => {
    updateLocalStateRaw(localState, "selectedDemerit")({});
    updateLocalStateRaw(
      localState,
      "isAddDemeritModalOpen"
    )(!isAddDemeritModalOpen);
  };

  const editDemerit = (demerit: Demerit) => () => {
    localState.merge({
      selectedDemerit: demerit,
      isAddDemeritModalOpen: true,
    });
  };

  const handleScroll = useCallback(() => {
    if (usersListRef.current) {
      const stateLocal = localState.get();
      const usersList = stateLocal.usersList || [];
      const total = stateLocal.total || 0;
      const loading = stateLocal.loading || false;
      const { scrollTop, scrollHeight, clientHeight } = usersListRef.current;
      console.log(
        scrollHeight,
        scrollTop,
        clientHeight,
        scrollHeight - scrollTop <= clientHeight * 1,
        usersList.length,
        total,
        usersList.length < total,
        loading
      );
      if (
        scrollHeight - scrollTop <= clientHeight * 1 &&
        usersList.length < total &&
        !loading
      ) {
        localState.merge({
          page: (stateLocal.page || 0) + 1,
        });
      }
    }
  }, []);

  useEffect(() => {
    if (usersListRef.current) {
      usersListRef.current.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (usersListRef.current) {
        usersListRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const onModalClose = () => {
    localState.isAddDemeritModalOpen.set(false);
  };

  return (
    <div className='bg-[#f8f9fd] flex gap-5 flex-1 overflow-y-auto'>
      <div
        className={`${
          isCollapsed ? "w-1/4" : "w-full"
        } transition-all bg-white p-4 rounded-lg flex flex-col`}
      >
        {/* make div under this comment be flex-col on collapse */}
        <div
          className={`flex justify-between pb-2 ${
            isCollapsed ? "flex-col gap-1" : "flex-row"
          }`}
        >
          <InputField
            value={search}
            handleChange={onSearchChange}
            type='text'
            placeholder='Поиск по студентам'
          />
          <DefaultButton
            disabled={multipleSelectedStudents.length <= 1}
            buttonText='Создать коллективное нарушение'
            additionalStyle='py-2 px-4'
            onClick={openDemeritModal}
          />
        </div>

        <div
          className='flex flex-col flex-1 overflow-y-auto'
          ref={usersListRef}
        >
          <table className='w-full border-collapse mt-4 rounded-lg overflow-hidden text-md'>
            <thead>
              <tr className='bg-white'>
                <th className='px-4 py-2 border-b w-[50px]'>
                  <input
                    type='checkbox'
                    checked={
                      multipleSelectedStudents?.length ===
                      studentsWithDemerits?.length
                    }
                    onChange={toggleSelectAllStudents}
                  />
                </th>
                {categoryList.map((category, index) => (
                  <th
                    key={index}
                    className={`px-4 py-2 border-b ${
                      category.additionalClassName
                    } ${isCollapsed && index > 1 ? "hidden" : ""}`}
                  >
                    {category.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {studentsWithDemerits.map((item, index) => (
                <tr
                  key={index}
                  className='border-b border-gray-200 bg-white-500 '
                >
                  <td className='px-4 py-2 bg-white max-w-[50px] align-center'>
                    <input
                      type='checkbox'
                      checked={multipleSelectedStudents?.some(
                        (s) => s.id === item.id
                      )}
                      onChange={() => handleMultipleSelectStudents(item)}
                    />
                  </td>
                  <td className='px-4 py-2 bg-white max-w-[50px] align-center'>
                    <DefaultButton
                      onClick={() => handleArrowClick(item)}
                      Icon={ArrowRightWithBody}
                      additionalStyle='px-2 py-2 bg-blue-100 opacity-[0.7] border-none text-blue-500 font-bold'
                      iconStyle={`transition-transform duration-300 opacity-[1] ${
                        selectedStudent?.id === item.id
                          ? "rotate-180"
                          : "rotate-0"
                      }`}
                    />
                  </td>
                  <td className='px-4 py-2 bg-white align-center'>
                    <div className='flex items-center gap-2'>
                      <Avatar user={item} />
                      <span>
                        {item.json_saved_data.firstname}{" "}
                        {item.json_saved_data.lastname}
                      </span>
                    </div>
                  </td>
                  {!isCollapsed && (
                    <>
                      <td className='px-4 py-2 bg-white text-end max-w-[100px]'>
                        {item.social_transcript_items.reduce(
                          socialTranscriptItemsReduce,
                          0
                        )}
                      </td>
                      <td className='px-4 py-2 bg-white'>
                        <p className='flex items-center gap-1 text-gray-400'>
                          <span
                            className={`${
                              0 > 3 ? "text-green-500" : "text-red-500"
                            } text-lg`}
                          >
                            {0}
                          </span>{" "}
                          / 5
                        </p>
                      </td>
                      <td className='px-4 py-2 bg-white'>
                        {item.demerits.length}
                      </td>
                      <td className='px-4 py-2 bg-white'>
                        {item.json_saved_data.email}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isCollapsed && selectedStudent && (
        <div className='w-3/4 rounded-lg bg-[#f8f9fd]'>
          <div className='bg-white p-4 rounded-lg flex items-center gap-4 mb-6'>
            <Avatar user={selectedStudent} />
            <div>
              <h2 className='text-2xl font-bold'>
                {selectedStudent.json_saved_data.firstname}{" "}
                {selectedStudent.json_saved_data.lastname}
              </h2>
              <p className='text-gray-500'>
                {selectedStudent.json_saved_data.email}
              </p>
            </div>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 rounded-lg mb-6'>
            <div className='flex flex-col items-start justify-between p-4 bg-white rounded-lg gap-3'>
              <div className='flex items-start justify-between w-full'>
                <span className='text-gray-500 font-medium'>
                  Социальный транскрипт
                </span>
                <DefaultButton
                  Icon={Star}
                  iconStyle='w-6 h-6'
                  additionalStyle='border-none bg-transparent text-gray-400 py-0.5 px-1'
                />
              </div>
              <div className={"text-3xl font-bold mt-2 text-blue-500"}>
                {selectedStudent.social_transcript_items.reduce(
                  socialTranscriptItemsReduce,
                  0
                )}
              </div>
            </div>
            {/* <div className="flex flex-col items-start justify-between p-4 bg-white rounded-lg gap-3">
              <div className="flex items-start justify-between w-full">
                <span className="text-gray-500 font-medium">ГПА</span>
                <DefaultButton
                  Icon={ChartBar}
                  iconStyle="w-6 h-6"
                  additionalStyle="border-none bg-transparent text-gray-400 py-0.5 px-1"
                />
              </div>
              <div className={'text-3xl font-bold text-green-500'}>
                {selectedStudent.gpa}{' '}
                <span className="text-[19px] font-normal text-gray-400">
                  {' '}
                  / 4
                </span>
              </div>
            </div> */}
            <div className='flex flex-col items-start justify-between p-4 bg-white rounded-lg gap-3'>
              <div className='flex items-start justify-between w-full'>
                <span className='text-gray-500 font-medium'>
                  Средняя оценка
                </span>
                <DefaultButton
                  Icon={Star}
                  iconStyle='w-6 h-6'
                  additionalStyle='border-none bg-transparent text-gray-400 py-0.5 px-1'
                />
              </div>
              <div className={"text-3xl font-bold mt-2 text-green-500"}>
                {0}
                <span className='text-[19px] font-normal text-gray-400'>
                  {" "}
                  %
                </span>
              </div>
            </div>
            <div className='flex flex-col items-start justify-between p-4 bg-white rounded-lg gap-3'>
              <div className='flex items-start justify-between w-full'>
                <span className='text-gray-500 font-medium'>
                  Оценка комнаты
                </span>
                <DefaultButton
                  Icon={Home}
                  iconStyle='w-6 h-6'
                  additionalStyle='border-none bg-transparent text-gray-400 py-0.5 px-1'
                />
              </div>
              <div className={"text-3xl font-bold mt-2 text-blue-500"}>
                {0}
                <span className='text-[19px] font-normal text-gray-400'>
                  {" "}
                  / 5
                </span>
              </div>
            </div>
          </div>

          <div className='bg-white p-4 rounded-lg gap-4 mb-6'>
            <div className='flex justify-between'>
              <h3 className='text-lg font-bold mb-4'>История выговоров</h3>
              <HasRole roles={["create_demerits"]}>
                <DefaultButton
                  additionalStyle='px-3 py-2 border border-blue-500 text-blue-500'
                  buttonText='Добавить нарушение'
                  Icon={Plus}
                  onClick={openDemeritModal}
                />
              </HasRole>
            </div>

            <table className='w-full table-fixed border-collapse mt-4 rounded-lg overflow-hidden'>
              <thead>
                <tr className='bg-white'>
                  <th className='px-4 py-2 border-b text-left w-[250px]'>
                    Тип выговора
                  </th>
                  <th className='px-4 py-2 border-b text-left w-[125px]'>
                    Дата создания
                  </th>
                  <th className='px-4 py-2 border-b text-left w-[175px]'>
                    Статус
                  </th>
                  <th className='px-4 py-2 border-b text-left'></th>
                </tr>
              </thead>
              <tbody>
                {selectedStudent.demerits
                  .filter((warning) =>
                    !userHasRole("view_demerits_extended")
                      ? warning.created_by === uniqUser.id
                      : true
                  )
                  .map((warning, index) => (
                    <tr key={index} className='border-b'>
                      <td className='px-4 py-2'>{warning.demerit_type.name}</td>
                      <td className='px-4 py-2'>
                        {getDateOnly(warning.createdAt)}
                      </td>
                      <td className='px-4 py-2'>
                        <span
                        // className={`font-semibold ${warning.status === 'Действует'
                        //   ? 'text-orange-500'
                        //   : warning.status === 'Завершён'
                        //     ? 'text-green-500'
                        //     : 'text-red-500'
                        //   }`}
                        >
                          {demeritStatusMapper[warning.status]}
                        </span>
                      </td>
                      <td className='px-4 py-2 max-w-[160px]'>
                        <DefaultButton
                          Icon={Edit}
                          buttonText='Редактировать'
                          additionalStyle='px-3 py-1 border border-gray-400 text-gray-400 hover:bg-gray-100'
                          onClick={editDemerit(warning)}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <Modal
        additionalClassName='w-[52%] h-[90%] rounded-xl p-8 flex flex-col'
        visible={isAddDemeritModalOpen}
        setVisible={updateLocalStateRaw(localState, "isAddDemeritModalOpen")}
      >
        <DisciplinaryDemerit
          parentState={localState}
          multiple={multipleSelectedStudents.length > 1}
          multipleSelectedStudents={multipleSelectedStudents}
          selectedStudent={selectedStudent}
          onClose={onModalClose}
        />
      </Modal>
    </div>
  );
};

export default DisciplinaryTableStudents;
